import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { properties } from '../services/api';
import axios from 'axios';
import GardenModal from './GardenModal';
import './SeedStarting.css';

const API_URL = process.env.REACT_APP_API_URL

function SeedStarting() {
  const [userProperties, setUserProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [userSelections, setUserSelections] = useState([]);
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [customPlant, setCustomPlant] = useState({ name: '', type: '', notes: '' });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ isOpen: false });
  const navigate = useNavigate();

  useEffect(() => {
    loadProperties();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProperties = async () => {
    try {
      const response = await properties.getAll();
      setUserProperties(response.data.properties);
      if (response.data.properties.length > 0) {
        const firstProp = response.data.properties[0];
        setSelectedProperty(firstProp);
        await loadRecommendations(firstProp.id);
        await loadSelections(firstProp.id);
      }
      setLoading(false);
    } catch (error) {
      console.error('SeedStarting: loadProperties failed:', error);
      setLoading(false);
    }
  };

  const loadRecommendations = async (propertyId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/tasks/seed-starting/${propertyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      let allRecommendations = response.data.recommendations;
      const customPlantsResponse = await axios.get(
        `${API_URL}/plant-selections/custom-plants/${propertyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (customPlantsResponse.data.customPlants) {
        const customPlants = customPlantsResponse.data.customPlants.map(cp => ({
          plantType: cp.plant_type || 'custom',
          commonName: cp.common_name,
          startIndoorsDate: null,
          transplantDate: null,
          directSowDate: null,
          daysToMaturity: null,
          notes: cp.custom_notes || 'Custom plant',
          harvestDate: null,
          isCustom: true
        }));
        allRecommendations = [...allRecommendations, ...customPlants];
      }
      setRecommendations(allRecommendations);
    } catch (error) {
      console.error('SeedStarting: loadRecommendations failed:', error);
      if (error.response?.status === 400) {
        setModal({
          isOpen: true, type: 'alert', title: 'Error',
          message: error.response.data.error,
          onConfirm: () => setModal({ isOpen: false })
        });
      }
    }
  };

  const loadSelections = async (propertyId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/plant-selections/property/${propertyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUserSelections(response.data.selections);
    } catch (error) {
      console.error('SeedStarting: loadSelections failed:', error);
    }
  };

  const toggleSelection = async (rec) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/plant-selections/toggle`,
        {
          propertyId: selectedProperty.id,
          commonName: rec.commonName,
          seedCalendarId: rec.seedCalendarId,
          plantType: rec.plantType
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.selected) {
        setUserSelections([...userSelections, rec.commonName]);
      } else {
        setUserSelections(userSelections.filter(name => name !== rec.commonName));
      }
    } catch (error) {
      console.error('SeedStarting: toggleSelection failed:', error);
      setModal({
        isOpen: true, type: 'alert', title: 'Error',
        message: 'Failed to update selection',
        onConfirm: () => setModal({ isOpen: false })
      });
    }
  };

  const addCustomPlant = async () => {
    if (!customPlant.name.trim()) {
      setModal({
        isOpen: true, type: 'alert', title: 'Required',
        message: 'Please enter a plant name',
        onConfirm: () => setModal({ isOpen: false })
      });
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/plant-selections/custom`,
        {
          propertyId: selectedProperty.id,
          commonName: customPlant.name,
          plantType: customPlant.type,
          customNotes: customPlant.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomPlant({ name: '', type: '', notes: '' });
      setShowAddCustom(false);
      setModal({
        isOpen: true, type: 'alert', title: 'Success',
        message: 'Custom plant added. Thank you for the suggestion.',
        onConfirm: () => setModal({ isOpen: false })
      });
      await loadSelections(selectedProperty.id);
      await loadRecommendations(selectedProperty.id);
    } catch (error) {
      console.error('SeedStarting: addCustomPlant failed:', error);
      setModal({
        isOpen: true, type: 'alert', title: 'Error',
        message: error.response?.data?.error || 'Failed to add custom plant',
        onConfirm: () => setModal({ isOpen: false })
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d.getDate()} ${months[d.getMonth()]}`;
  };

  const isUpcoming = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const now = new Date();
    const twoWeeks = new Date(now.getTime() + (14 * 24 * 60 * 60 * 1000));
    return date >= now && date <= twoWeeks;
  };

  const filteredRecommendations = showOnlySelected
    ? recommendations.filter(rec => userSelections.includes(rec.commonName))
    : recommendations;

  if (loading) return <div className="seed-loading">Loading...</div>;

  if (userProperties.length === 0) {
    return (
      <div className="seed-container seed-container--sm">
        <h2>Seed Starting Calendar</h2>
        <div className="seed-empty">
          <p>Create a property first to see personalized planting dates.</p>
          <button className="seed-empty-btn" onClick={() => navigate('/properties')}>
            Go to Properties
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="seed-container">
      <GardenModal isOpen={modal.isOpen} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} onCancel={() => setModal({ isOpen: false })} />
      <h2>Seed Starting Calendar</h2>

      <div className="seed-controls">
        {userProperties.length > 1 && (
          <div>
            <label className="seed-prop-label">Property: </label>
            <select
              className="seed-prop-select"
              value={selectedProperty?.id}
              onChange={(e) => {
                const prop = userProperties.find(p => String(p.id) === String(e.target.value));
                setSelectedProperty(prop);
                loadRecommendations(e.target.value);
                loadSelections(e.target.value);
              }}
            >
              {userProperties.map(prop => (
                <option key={prop.id} value={prop.id}>{prop.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="seed-btn-group">
          <button
            className={`seed-toggle-btn${showOnlySelected ? ' seed-toggle-btn--active' : ''}`}
            onClick={() => setShowOnlySelected(!showOnlySelected)}
          >
            {showOnlySelected ? 'Show All Plants' : 'My Plants Only'}
          </button>
          <button className="seed-add-btn" onClick={() => setShowAddCustom(true)}>
            + Add Custom Plant
          </button>
        </div>
      </div>

      {selectedProperty && (
        <div className="seed-property-card">
          <p className="seed-property-name">
            {selectedProperty.name}{selectedProperty.usda_zone ? ` · Zone ${selectedProperty.usda_zone}` : ''}
          </p>
          {(selectedProperty.lastFrostDate || selectedProperty.firstFrostDate) && (
            <p className="seed-frost-dates">
              {selectedProperty.lastFrostDate && `Last Frost: ${formatDate(selectedProperty.lastFrostDate)}`}
              {selectedProperty.lastFrostDate && selectedProperty.firstFrostDate && ' · '}
              {selectedProperty.firstFrostDate && `First Frost: ${formatDate(selectedProperty.firstFrostDate)}`}
            </p>
          )}
          {userSelections.length > 0 && (
            <p className="seed-selections-count">{userSelections.length} plants selected</p>
          )}
        </div>
      )}

      {showAddCustom && (
        <div className="seed-modal-overlay">
          <div className="seed-modal-dialog">
            <h3>Add Custom Plant</h3>
            <div className="seed-modal-field">
              <label className="seed-modal-label">Plant Name *</label>
              <input
                className="seed-modal-input"
                type="text"
                value={customPlant.name}
                onChange={(e) => setCustomPlant({...customPlant, name: e.target.value})}
                placeholder="e.g., Heirloom Tomato"
              />
            </div>
            <div className="seed-modal-field">
              <label className="seed-modal-label">Plant Type</label>
              <input
                className="seed-modal-input"
                type="text"
                value={customPlant.type}
                onChange={(e) => setCustomPlant({...customPlant, type: e.target.value})}
                placeholder="e.g., vegetable, herb, flower"
              />
            </div>
            <div className="seed-modal-field seed-modal-field--last">
              <label className="seed-modal-label">Notes (optional)</label>
              <textarea
                className="seed-modal-textarea"
                value={customPlant.notes}
                onChange={(e) => setCustomPlant({...customPlant, notes: e.target.value})}
                placeholder="Any special requirements or notes..."
                rows="3"
              />
            </div>
            <div className="seed-modal-actions">
              <button className="seed-modal-btn-confirm" onClick={addCustomPlant}>Add Plant</button>
              <button
                className="seed-modal-btn-cancel"
                onClick={() => { setShowAddCustom(false); setCustomPlant({ name: '', type: '', notes: '' }); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="seed-list">
        {[...filteredRecommendations].sort((a, b) => userSelections.includes(b.commonName) - userSelections.includes(a.commonName)).map((rec, idx) => {
          const isSelected = userSelections.includes(rec.commonName);
          return (
            <div
              key={idx}
              className={`seed-rec-card${isSelected ? ' seed-rec-card--selected' : ''}`}
              onClick={() => toggleSelection(rec)}
            >
              <div className="seed-rec-inner">
                <div className="seed-rec-info">
                  <div className="seed-rec-name-row">
                    <strong className="seed-rec-name">{rec.commonName}</strong>
                    {rec.plantType && (
                      <span className="seed-type-badge">{rec.plantType}</span>
                    )}
                  </div>
                  <div className="seed-dates">
                    {rec.startIndoorsDate && (
                      <span className={`seed-date-badge${isUpcoming(rec.startIndoorsDate) ? ' seed-date-badge--upcoming' : ''}`}>
                        Start indoors: {formatDate(rec.startIndoorsDate)}
                      </span>
                    )}
                    {rec.transplantDate && <span>Transplant: {formatDate(rec.transplantDate)}</span>}
                    {rec.directSowDate && (
                      <span className={`seed-date-badge${isUpcoming(rec.directSowDate) ? ' seed-date-badge--upcoming-blue' : ''}`}>
                        Direct sow: {formatDate(rec.directSowDate)}
                      </span>
                    )}
                    {rec.harvestDate && <span className="seed-harvest-date">Harvest: {formatDate(rec.harvestDate)}</span>}
                    {rec.daysToMaturity && <span className="seed-days-count">{rec.daysToMaturity} days</span>}
                  </div>
                  {rec.notes && <div className="seed-rec-notes">{rec.notes}</div>}
                </div>
                <div className={`seed-status-badge${isSelected ? ' seed-status-badge--selected' : ''}`}>
                  {isSelected ? 'Growing this year' : '+ Add to my garden'}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredRecommendations.length === 0 && showOnlySelected && (
        <div className="seed-empty-selected">
          No plants selected yet. Toggle "Show All Plants" and tap plants you want to grow.
        </div>
      )}
    </div>
  );
}

export default SeedStarting;
